import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNode, useEditor } from '@splashthat/splash-cms-engine';
import TextEditable from 'common/components/TouchpointEditor/TextEditable';

import { BRAND_LIBRARY_LIGHT_TEXT_COLOR, BRAND_LIBRARY_HEADLINE_TEXT } from 'common/constants/brandLibrary';

import { useTouchpointData } from 'common/components/TouchpointEditor/contexts/TouchpointDataContext';
import useDynamicTags from 'common/components/TouchpointEditor/hooks/useDynamicTags';
import { getCustomCssClassName } from 'common/components/TouchpointEditor/tools/CustomCssTools/CustomCssTools';

import { HeadlineContent } from './Headline.styles';

const selectIsEditing = (state) => ({ editing: state.editing });

const Headline = React.forwardRef(
    (
        {
            styles: { fontSize, fontColor, fontFamily },
            meta: { content, visible },
            as,
            contentEditable,
            selected,
            hovered,
            className,
        },
        ref,
    ) => {
        const { theme } = useTouchpointData();
        const node = useNode();
        const editor = useEditor(selectIsEditing);

        const customCssClassName = getCustomCssClassName(node);
        const joinedClassNames = [className, customCssClassName || ''].join(' ');

        const dynamicContent = useDynamicTags(content);
        const setContent = useCallback(
            (text) =>
                node.actions.setProp((editedNode) => {
                    editedNode.meta.content = text; // eslint-disable-line no-param-reassign
                    return editedNode;
                }),
            [],
        );

        return visible ? (
            <HeadlineContent
                as={as}
                $fontColor={theme.colors[fontColor]}
                $fontFamily={theme.activeFonts[fontFamily]}
                $fontSize={fontSize}
                hovered={hovered}
                onClick={node.actions.onClick()}
                onMouseLeave={node.actions.onMouseLeave()}
                onMouseMove={node.actions.onMouseMove()}
                selected={selected}
                className={joinedClassNames}
                ref={ref}
            >
                <TextEditable
                    setContent={setContent}
                    disabled={!contentEditable || !editor.editing || (!selected && !hovered)}
                >
                    {dynamicContent}
                </TextEditable>
            </HeadlineContent>
        ) : null;
    },
);

Headline.splash = {
    related: {
        settings: React.lazy(() => import('./Headline.tools')),
    },
    defaultProps: {
        styles: {
            fontSize: 24,
            fontColor: BRAND_LIBRARY_LIGHT_TEXT_COLOR,
            fontFamily: BRAND_LIBRARY_HEADLINE_TEXT,
        },
        meta: {
            content: 'Save your spot',
            visible: true,
        },
    },
};

Headline.propTypes = {
    selected: PropTypes.bool.isRequired,
    hovered: PropTypes.bool.isRequired,
    styles: PropTypes.shape({
        fontFamily: PropTypes.number,
        fontSize: PropTypes.number,
        fontColor: PropTypes.number,
    }).isRequired,
    meta: PropTypes.shape({
        content: PropTypes.string,
        visible: PropTypes.bool,
    }).isRequired,
    as: PropTypes.string,
    contentEditable: PropTypes.bool,
    className: PropTypes.string,
};

Headline.defaultProps = {
    as: '',
    contentEditable: true,
    className: '',
};

export default Headline;
